import { createRouter, createWebHistory } from "vue-router";
import { projectAuth } from "../firebase/config";
import store from "../store";
import { trackPageView } from "../composables/useGoogleAnalytics";
import { buildDashboardRoute, resolveDashboardTab } from "../composables/dashboardNavigation";
import { hasSubscriptionAccess, applySubscriptionEntitlements } from "../composables/subscriptionEntitlements";

import { applyRoutePageTitle } from "../composables/routePageTitle";

import { powerhousePurchaseRoute } from '../composables/purchaseBilling';

// auth guard

const requireAuth = (to, from, next) => {
  let user = projectAuth.currentUser;
  if (!user) {
    next({ name: "Login" });
  } else {
    next();
  }
};

const resolveAccountOwnerUid = async (authUid) => {
  try {
    const { doc, getDoc } = await import("firebase/firestore");
    const { projectFirestore } = await import("../firebase/config");
    const membershipRef = doc(projectFirestore, "accountMemberships", authUid);
    const membershipSnap = await getDoc(membershipRef);

    if (!membershipSnap.exists()) return authUid;

    const membership = membershipSnap.data() || {};
    const membershipStatus = (membership.status || "active").toString().toLowerCase();
    if (membershipStatus !== "active") {
      throw new Error("Account membership is suspended.");
    }

    return membership.ownerUid || authUid;
  } catch (error) {
    if (error.message === "Account membership is suspended.") {
      throw error;
    }
    return authUid;
  }
};

const redirectSubscribedPurchase = async (to, from, next) => {
  const user = projectAuth.currentUser;
  if (!user) return next();
  try {
    const { doc, getDoc } = await import("firebase/firestore");
    const { projectFirestore } = await import("../firebase/config");
    const ownerUid = await resolveAccountOwnerUid(user.uid);
    const snap = await getDoc(doc(projectFirestore, "users", ownerUid));
    const data = snap.data() || {};
    if (!hasSubscriptionAccess(data)) return next();
    if (data.configComplete === true || data.configComplete === "true") return next(buildDashboardRoute(to));
    return next({ name: "Apply" });
  } catch (error) {
    console.error("Could not resolve Powerhouse purchase state", error);
    return next();
  }
};

const routes = [
  {
    path: "/",
    name: "Login",
    component: () =>
      import(/* webpackChunkName: "login" */ "../views/newLogin.vue"),
  },
  {
    path: "/login/team",
    name: "TeamLogin",
    component: () =>
      import(/* webpackChunkName: "login" */ "../views/newLogin.vue"),
  },
  {
    path: "/auth/action",
    name: "AuthAction",
    component: () =>
      import(/* webpackChunkName: "auth-action" */ "../views/AuthActionHandler.vue"),
  },
  {
    path: "/verify-email",
    name: "VerifyEmail",
    component: () =>
      import(/* webpackChunkName: "verify-email" */ "../views/VerifyEmail.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/auth/portal-continue",
    name: "PortalContinue",
    component: () =>
      import(/* webpackChunkName: "portal-continue" */ "../views/PortalContinue.vue"),
  },
  {
    path: "/account-validation",
    name: "AccountValidation",
    component: () =>
      import(/* webpackChunkName: "account-validation" */ "../components/AccountValidation.vue"),
    meta: { requiresAuth: true },
    beforeEnter: requireAuth,
  },
  {
    path: "/square-callback",
    name: "SquareCallback",
    component: () =>
      import(/* webpackChunkName: "square-callback" */ "../views/SquareCallback.vue"),
    meta: { requiresAuth: true },
    beforeEnter: requireAuth,
  },
  {
    path: "/google-analytics-callback",
    name: "GoogleAnalyticsCallback",
    component: () =>
      import(/* webpackChunkName: "google-analytics-callback" */ "../views/GoogleAnalyticsCallback.vue"),
    meta: { requiresAuth: true },
    beforeEnter: requireAuth,
  },
  {
    path: "/google-calendar-callback",
    name: "GoogleCalendarCallback",
    component: () =>
      import(/* webpackChunkName: "google-calendar-callback" */ "../views/GoogleCalendarCallback.vue"),
    meta: { requiresAuth: true },
    beforeEnter: requireAuth,
  },
  {
    path: "/dialpad-callback",
    name: "DialpadCallback",
    component: () =>
      import(/* webpackChunkName: "dialpad-callback" */ "../views/DialpadCallback.vue"),
    /** No requireAuth: Firebase session may not be synchronized when the page first loads; the view waits for auth. */
    meta: { requiresAuth: false },
  },
  {
    path: "/book-demo",
    name: "BookDemo",
    redirect: { name: "Pricing" },
  },
  {
    path: "/affiliate-program",
    name: "Affiliates",
    component: () =>
      import(/* webpackChunkName: "affiliates" */ "../views/Affiliates.vue"),
  },
  {
    path: "/pricing",
    name: "Pricing",
    component: () =>
      import(/* webpackChunkName: "pricing" */ "../views/Pricing.vue"),
    beforeEnter: async (to, from, next) => {
      const user = projectAuth.currentUser;

      if (user) {
        try {
          const { doc, getDoc } = await import("firebase/firestore");
          const { projectFirestore } = await import("../firebase/config");
          const ownerUid = await resolveAccountOwnerUid(user.uid);
          const userRef = doc(projectFirestore, "users", ownerUid);
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const configComplete = userData.configComplete === true || userData.configComplete === 'true';
            const hasSubscription = hasSubscriptionAccess(userData);

            // Fully onboarded users go straight to Dashboard
            if (configComplete && hasSubscription) {
              next(buildDashboardRoute(to));
              return;
            }

            // Subscribed but not configured → go set up their business
            if (hasSubscription && !configComplete) {
              next({ name: "Apply" });
              return;
            }

            if (store.state.whiteLabelClient?.accountType !== 'client') {
              next(powerhousePurchaseRoute(to.query, userData));
              return;
            }
          }
        } catch (error) {
          console.error('Error in Pricing route guard:', error);
          if (error.message === "Account membership is suspended.") {
            next({ name: "Login" });
            return;
          }
        }
      }

      // Allow access for everyone else (new users, unsubscribed, logged-out)
      next();
    }
  },
  {
    path: "/purchase/powerhouse",
    name: "PurchasePowerhouse",
    component: () => import(/* webpackChunkName: "purchase" */ "../views/PurchasePowerhouse.vue"),
    beforeEnter: redirectSubscribedPurchase,
  },
  {
    path: "/purchase/powerhouse/success",
    name: "PowerhousePurchaseSuccess",
    component: () => import(/* webpackChunkName: "purchase" */ "../views/PowerhousePurchaseSuccess.vue"),
  },
  {
    path: "/purchase/shadow/activate",
    name: "ShadowActivation",
    component: () => import(/* webpackChunkName: "purchase" */ "../views/ShadowActivation.vue"),
  },
  {
    path: "/apply",
    name: "Apply",
    component: () =>
      import(/* webpackChunkName: "apply" */ "../views/Apply.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: () =>
      import(/* webpackChunkName: "dashboard" */ "../views/Dashboard.vue"),
    beforeEnter: async (to, from, next) => {
      const user = projectAuth.currentUser;
      if (!user) {
        next({ name: "Login" });
        return;
      }

      // AccountValidation / bootstrapAuthSession already synced subscription/config into the store.
      // Re-running Firestore + dynamic imports here can stall navigation (await router.replace never resolves)
      // with no console error. Trust the store when session data is already loaded.
      if (isFullyOnboardedInStore()) {
        next();
        return;
      }

      if (
        from.name === "AccountValidation" &&
        store.state.hasSubscribed &&
        store.state.userConfig?.configComplete
      ) {
        next();
        return;
      }

      try {
        const { doc, getDoc } = await import("firebase/firestore");
        const { projectFirestore } = await import("../firebase/config");
        const ownerUid = await resolveAccountOwnerUid(user.uid);
        const userRef = doc(projectFirestore, "users", ownerUid);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
          next({ name: "AccountValidation" });
          return;
        }

        const userData = userDoc.data();
        const hasSubscription = hasSubscriptionAccess(userData);
        const configComplete = (userData.configComplete === true || userData.configComplete === 'true') || store.state.userConfig?.configComplete;

        // Sync store state
        applySubscriptionEntitlements(store, userData);
        if (!store.state.userConfig?.configComplete && configComplete) {
          if (!store.state.userConfig) store.state.userConfig = {};
          store.state.userConfig.configComplete = configComplete;
        }

        // Fully onboarded → allow Dashboard
        if (hasSubscription && configComplete) {
          next();
          return;
        }

        // Subscribed but not configured → finish setup
        if (hasSubscription && !configComplete) {
          next({ name: "Apply" });
          return;
        }

        // Not subscribed → go to Pricing (regardless of configComplete)
        next({ name: "Pricing" });
      } catch (error) {
        console.error("Error loading user data in router guard:", error);
        if (error.message === "Account membership is suspended.") {
          next({ name: "Login" });
          return;
        }
        next({ name: "AccountValidation" });
      }
    },
  },
  {
    path: "/documentation",
    name: "Documentation",
    component: () =>
      import(/* webpackChunkName: "documentation" */ "../components/Documentation/DocumentationUI.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/dashboard/documentation",
    name: "DashboardDocumentation",
    component: () =>
      import(/* webpackChunkName: "documentation" */ "../components/Documentation/DocumentationUI.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/guides",
    name: "Guides",
    redirect: { name: "Dashboard", query: { tab: "guides" } },
  },
  {
    path: "/dashboard/guides",
    name: "DashboardGuides",
    redirect: { name: "Dashboard", query: { tab: "guides" } },
  },
  {
    path: "/settings",
    name: "Settings",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../components/BackOffice/Settings/SettingsUI.vue"),
    beforeEnter: requireAuth,
  },
  {
    path: "/feedback",
    name: "Feedback",
    redirect: (to) => {
      // Set feedback tab active in store and redirect to dashboard
      // Reset all views
      Object.keys(store.state.currentDashboardView).forEach(key => {
        store.state.currentDashboardView[key] = false;
      });
      // Set feedback tab active
      store.state.currentDashboardView.feedback = true;
      store.state.dashBoardView = "Feedback";
      return { name: "Dashboard", query: { tab: "feedback" } };
    },
    beforeEnter: requireAuth,
  },
  {
    path: "/privacy-policy",
    name: "PrivacyPolicy",
    component: () =>
      import(
        /* webpackChunkName: "privacy-policy" */ "../views/PrivacyPolicy.vue"
      ),
  },
  {
    path: "/tos",
    name: "TOS",
    component: () => import(/* webpackChunkName: "tos" */ "../views/TOS.vue"),
  },
  {
    path: "/cookie-policy",
    name: "CookiePolicy",
    component: () => import(/* webpackChunkName: "cookie-policy" */ "../views/CookiePolicy.vue"),
  },
  {
    path: "/data-processing-agreement",
    name: "DataProcessingAgreement",
    component: () => import(/* webpackChunkName: "data-processing-agreement" */ "../views/DataProcessingAgreement.vue"),
  },
  {
    path: "/data-anonymization-policy",
    name: "DataAnonymizationPolicy",
    component: () => import(/* webpackChunkName: "data-anonymization-policy" */ "../views/DataAnonymizationPolicy.vue"),
  },
  {
    path: "/brands",
    name: "Brands",
    component: () => import(/* webpackChunkName: "brands" */ "../views/Brands.vue"),
    meta: {
      title: "Window Tint Film Brands | Tint Bolt",
    },
  },
  {
    path: "/brands/:brandName",
    name: "BrandDetail",
    component: () => import(/* webpackChunkName: "brand-detail" */ "../views/BrandDetail.vue"),
    meta: {
      title: "Brand Details | Tint Bolt",
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// OAuth callback routes: allow through even before store.state.currentUser is set
// (Firebase may not have restored session yet on redirect). The callback page will wait for auth.
const oauthCallbackRouteNames = ['DialpadCallback', 'GoogleCalendarCallback', 'SquareCallback', 'GoogleAnalyticsCallback'];
// Checkout can finish before verification, but shop activation cannot begin
// until Firebase confirms the user controls the email address.
const verificationDeferredRouteNames = ['PurchasePowerhouse', 'PowerhousePurchaseSuccess'];

const hasStoreUser = () => !!store.state.currentUser?.uid;
const hasFirebaseSession = () => !!projectAuth.currentUser;
const hasVerifiedEmail = () => projectAuth.currentUser?.emailVerified === true;

const isFullyOnboardedInStore = () => {
  const configComplete =
    store.state.userConfig?.configComplete === true ||
    store.state.userConfig?.configComplete === "true";
  const hasSubscription = hasSubscriptionAccess({
    ...(store.state.userConfig || {}),
    subscriptionStatus: store.state.subscriptionStatus ?? store.state.userConfig?.subscriptionStatus,
    subscription: store.state.hasSubscribed || store.state.userConfig?.subscription,
  });
  return store.state.infoLoaded && hasSubscription && configComplete;
};

router.beforeEach((to, from, next) => {
  // Keep ?tab= on dashboard navigations (refresh, login handoff, router.replace desync)
  if (to.name === "Dashboard" && !to.query?.tab) {
    const tab = resolveDashboardTab(from) || resolveDashboardTab(to);
    if (tab) {
      next({ ...to, query: { ...to.query, tab } });
      return;
    }
  }

  // Check if route explicitly requires auth (only if requiresAuth is explicitly true)
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth === true);
  
  // Explicitly allow Guides and Documentation routes without auth
  if (to.name === 'Guides' || to.name === 'DashboardGuides' || to.name === 'Documentation' || to.name === 'DashboardDocumentation') {
    next();
    return;
  }

  // Firebase email action links (password reset, verify email, etc.)
  if (to.name === "AuthAction" || to.name === "PortalContinue") {
    next();
    return;
  }

  // OAuth callbacks: let the page load so it can wait for auth and then exchange the code
  if (oauthCallbackRouteNames.includes(to.name)) {
    next();
    return;
  }

  const authed = hasStoreUser() || hasFirebaseSession();

  if ((requiresAuth || to.name === 'BookDemo') && !authed) {
    next({ name: 'Login', query: { redirect: to.fullPath } });
  } else if (hasFirebaseSession() && !hasVerifiedEmail() && to.name !== 'VerifyEmail' && !verificationDeferredRouteNames.includes(to.name)) {
    next({ name: 'VerifyEmail', query: { continue: to.fullPath } });
  } else if (!requiresAuth && authed && (to.name === 'Login' || to.name === 'TeamLogin')) {
    // Check if user has completed setup before redirecting to Dashboard
    // If not, send them through AccountValidation to determine correct route
    const configComplete = store.state.userConfig?.configComplete;
    const hasSubscription = store.state.hasSubscribed;
    
    // Only redirect to Dashboard if user has completed setup
    // Otherwise, send to AccountValidation to check account state
    if (hasStoreUser() && configComplete && hasSubscription) {
      next(buildDashboardRoute(to));
    } else {
      // User hasn't completed setup, send to AccountValidation
      next({ name: 'AccountValidation', query: to.query });
    }
  } else {
    next();
  }
});

// Track page views with Google Analytics
router.afterEach((to, from, failure) => {
  applyRoutePageTitle(to, failure);
  // Track page view in Google Analytics
  trackPageView(to.name || 'Unknown Page', window.location.href);
});

export default router;
